import { scrollNearBoarder } from "@/components/utils/scroll";
import { asLen, windowFromBoard } from "@/math/coordinate-transform";
import { distance2, minus, plus } from "@/math/coordinates";
import { dragStartDist } from "@/model/Settings";
import type { IdMap } from "@/model/baseTypes";
import type {
  BoardCoordinate,
  ViewportCoordinate,
  WindowCoordinate,
  WindowLen,
} from "@/model/coordinates";
import { initWindowCoord } from "@/model/coordinates";
import {
  boundsCoord,
  boundsRect,
  clientCoord,
  elemOffsetCoord,
  elemScrollPos,
  offsetSize,
  scrollSize,
  viewportFromWindow,
  windowFromViewport,
} from "@/utils/dom/coordinates";
import { eventHandlerElem, eventTarget, isLeftClick, releaseCapture } from "@/utils/dom/dom";
import { pointerDown } from "@/utils/dom/pointer";
import { firstValue } from "@/utils/record";

const drags: IdMap<Drag> = {};

//TODO should we use Viewport coords?
export interface Drag {
  offset: WindowCoordinate; // the offset between mouse pos and drag.pos
  pos: WindowCoordinate; // logical drag position: pos = mousePos - offset
  el: HTMLElement;
  cursor: string;
  imageEl: HTMLElement;
  target: HTMLElement;
  scrollable?: Scrollable;
  pointerId: number;
  startFn: (d: Drag) => boolean | void;
  moveFn: (d: Drag) => WindowCoordinate | BoardCoordinate;
  stopFn: (d: Drag) => WindowCoordinate | BoardCoordinate | void;
  dragging: boolean;
  setOffset: (mode: "zero" | "elRelative") => void;
  setPos: (pos: ViewportCoordinate) => void;
  originalPos: () => ViewportCoordinate;
}

export function isDragging() {
  return firstValue(drags)?.dragging;
}

export interface DragHandlers {
  start: (d: Drag) => boolean | void;
  move: (d: Drag) => WindowCoordinate | BoardCoordinate;
  stop: (d: Drag) => WindowCoordinate | BoardCoordinate | void;
}

export function registerDrag(
  event: PointerEvent,
  options: DragHandlers & { imageEl?: HTMLElement | null },
) {
  releaseCapture(event);
  if (!isLeftClick(event)) {
    return;
  }

  if (options.imageEl) {
    const imageStyle = options.imageEl.style;
    imageStyle.position = "absolute";
    imageStyle.pointerEvents = "none";
  }

  event.stopPropagation();

  const id = event.pointerId;
  const el = eventHandlerElem(event);
  const pos = clientCoord(event);

  pointerDown(id, pos, true);
  const scrollable = findScrollable(el);
  drags[id] = {
    // make offset=mouse pos so that pos is 0,0
    offset: unscrollPos(windowFromViewport(pos), scrollable),
    pos: initWindowCoord(),
    el,
    cursor: el.style.cursor,
    imageEl: options.imageEl || el,
    target: eventTarget(event),
    scrollable,
    startFn: options.start,
    moveFn: options.move,
    stopFn: options.stop,
    pointerId: id,
    dragging: false,
    setOffset(mode: "zero" | "elRelative") {
      this.offset =
        mode === "zero"
          ? initWindowCoord()
          : minus(this.offset, windowFromBoard(elemOffsetCoord(this.el)));
    },
    setPos(pos) {
      this.pos = minus(windowFromViewport(pos), this.offset);
    },
    originalPos() {
      return viewportFromWindow(plus(this.pos, this.offset));
    },
  };
}

function unscrollPos(pos: WindowCoordinate, scrollable?: Scrollable): WindowCoordinate {
  return scrollable ? plus(pos, elemScrollPos(scrollable.el)) : pos;
}

export function handleDragPointerMove(
  id: number,
  pos: ViewportCoordinate,
  moveFn: (id: number, pos: ViewportCoordinate) => void,
) {
  const drag = drags[id];
  if (!drag) {
    return;
  }
  drag.setPos(pos);
  if (drag.scrollable) {
    drag.pos = plus(drag.pos, elemScrollPos(drag.scrollable.el));
    scrollInScrollable(pos, drag.scrollable, () => moveFn(id, pos));
  } else {
    scrollNearBoarder(pos);
  }
  if (drag.dragging) {
    setPosition(drag.imageEl, drag.moveFn(drag));
  } else if (distance2(drag.pos, initWindowCoord()) > dragStartDist) {
    drag.el.style.cursor = "grabbing";
    drag.dragging = true;
    if (drag.el !== drag.imageEl) {
      setPosition(drag.imageEl, drag.pos);
      const imageDist = asLen(minus(boundsCoord(drag.el), boundsCoord(drag.imageEl)));
      drag.offset = minus(drag.offset, imageDist);
      drag.pos = plus(drag.pos, imageDist);
      setPosition(drag.imageEl, drag.pos);
      drag.imageEl.style.width = drag.el.offsetWidth + "px";
      drag.imageEl.style.height = drag.el.offsetHeight + "px";
    }
    if (drag.startFn(drag) === false) {
      release(id);
    }
  }
}

export function handleDragPointerUp(id: number, pos?: ViewportCoordinate, target?: HTMLElement) {
  const drag = drags[id];
  if (!drag) {
    return;
  }
  try {
    if (!pos) {
      return;
    }
    if (drag.dragging) {
      // if we dragged a link, avoid following it
      if (target?.nodeName === "A") {
        target.onclick = () => false;
        window.setTimeout(() => (target.onclick = null), 100);
      }
      drag.setPos(pos);
      const stopPos = drag.stopFn(drag);
      if (stopPos) {
        setPosition(drag.imageEl, stopPos);
      }
    }
  } finally {
    release(id);
  }
}

function setPosition(el: HTMLElement, pos: WindowCoordinate | BoardCoordinate) {
  el.style.left = pos.x + "px";
  el.style.top = pos.y + "px";
}

function release(id: number) {
  drags[id].el.style.cursor = drags[id].cursor;

  // fix for the position of the link drag in the new sticky note
  if (drags[id].el.hasAttribute("data-sticky-note")) {
    drags[id].el.style.left = "";
    drags[id].el.style.top = "";
  }

  delete drags[id];
}

interface Scrollable {
  el: HTMLElement;
  scrollSize: WindowLen;
  timeout?: number;
}

function findScrollable(el: HTMLElement | null): Scrollable | undefined {
  while (el !== null) {
    if (el.classList.contains("scrollable")) {
      return { el, scrollSize: minus(scrollSize(el), offsetSize(el)) };
    }
    el = el.parentElement;
  }
}

function scrollInScrollable(pos: ViewportCoordinate, scrollable: Scrollable, recur: () => void) {
  window.clearTimeout(scrollable.timeout);
  const rect = boundsRect(scrollable.el);
  if (pos.y - rect.p0.y < 20) {
    scrollBy(-2);
  } else if (
    rect.p0.y + rect.size.y - pos.y < 20 &&
    scrollable.el.scrollTop < scrollable.scrollSize.y
  ) {
    scrollBy(2);
  }

  function scrollBy(delta: number) {
    scrollable.el.scrollBy({ top: delta });
    scrollable.timeout = window.setTimeout(recur, 20);
  }
}
